// src/widgets/widgetConfig.ts

import { SpecialMessageWidget } from "./SpecialMessageWidget";
import { UserStatsWidget } from "./UserStatsWidget";
import { SpeedGunWidget } from "./SpeedGunWidget";
import { TestControlsWidget } from "./TestControlsWidget";
import { ScoreByRoundWidget } from "./ScoreByRoundWidget";
import { TurnScore } from "../index";

/**
 * Represents the current state of the game, passed to widget predicates.
 */
export interface WidgetGameState {
    round: number;
    roll: number; // The current ball number in the turn (e.g., 1, 2, 3)
    turnHits: number[]; // Points scored in the current turn
    scores: number[];
    currentPlayerId: string;
    currentPlayer?: number; // Current player index (0 or 1)
    testMode?: boolean; // Add test mode flag
    turnScoreHistory?: TurnScore[]; // Score history for score-by-round widget
    player1Name?: string;
    player2Name?: string;
    phase?: 'normal' | 'over' | 'retribution' | 'sudden_death';
    bustHistory?: Array<{ player: 0 | 1; round: number }>;
    // Add other relevant game state properties here
}

/**
 * A function that determines if a widget should be shown based on game state.
 * @param gameState The current state of the game.
 * @returns {boolean} True if the widget should be shown.
 */
export type WidgetPredicate = (gameState: WidgetGameState) => boolean;

/**
 * Configuration for a single widget, whether timed or triggered.
 */
export interface WidgetConfig {
    id: string;
    component: (data?: any) => HTMLElement;
    title: string;
    fetchData?: (gameState: WidgetGameState) => Promise<any>;
}

/**
 * Configuration for a widget that is shown based on specific game state conditions.
 */
export interface TriggeredWidget {
    widgetId: string;
    predicate: WidgetPredicate;
    duration: number; // How long to show the widget for in ms
    priority: number; // Higher priority widgets are checked first
}

/**
 * Defines the complete configuration for a side panel's widget display.
 */
export interface PanelConfig {
    /** The list of widget IDs to cycle through by default on a timer. */
    timedWidgets: string[];
    /** The time in milliseconds to display each timed widget. */
    timedDuration: number;
    /** The widget to show when no other widgets are active. */
    defaultWidget: string;
    /** Widgets that are shown based on specific game state conditions. */
    triggeredWidgets?: TriggeredWidget[];
    /** Optional predicate to determine if the entire panel should be visible */
    panelPredicate?: WidgetPredicate;
}

// --- Widget Registry ---
// All available widgets must be registered here.
export const WIDGET_REGISTRY: { [id: string]: Omit<WidgetConfig, 'id'> } = {
    userStats: {
        component: UserStatsWidget,
        title: "User Stats",
    },
    specialMessage: {
        component: SpecialMessageWidget,
        title: "Pro Tip",
    },
    speedGun: {
        component: SpeedGunWidget,
        title: "Ball Speed",
    },
    testControls: {
        component: (data) => TestControlsWidget({
            speed: data?.speed ?? 455,
            vx: data?.vx ?? 0,
            vy: data?.vy ?? -455,
            spin: data?.spin ?? 0,
            tiltX: data?.tiltX ?? 0,
            onUpdate: data?.onUpdate,
        }),
        title: "Test Controls",
        fetchData: async (gameState) => {
            // Get test params from window global
            const app = (window as any).skeechApp;
            return {
                speed: app?.testParams?.speed ?? 455,
                vx: app?.testParams?.vx ?? 0,
                vy: app?.testParams?.vy ?? -455,
                spin: app?.testParams?.spin ?? 0,
                tiltX: app?.testParams?.tiltX ?? 0,
                onUpdate: (params: any) => {
                    if (app && app.testParams) {
                        app.testParams = params;
                    }
                },
            };
        },
    },
    scoreByRound: {
        component: ScoreByRoundWidget,
        title: "",
    fetchData: async (gameState) => {
        return {
            turnScoreHistory: gameState?.turnScoreHistory || [],
            player1Name: gameState?.player1Name || 'P1',
            player2Name: gameState?.player2Name || 'P2',
            currentScores: (gameState?.scores as [number, number]) || [0, 0],
            phase: gameState?.phase || 'normal',
            currentRound: gameState?.round || 1,
            bustHistory: gameState?.bustHistory || [],
        };
    },
    },
};


// --- Panel Configurations ---
// This is where you customize the behavior of the left and right panels.

export const sidePanelConfig = {
    leftPanel: {
        timedWidgets: ['scoreByRound'],
        timedDuration: 9999999, // Show each widget for 8 seconds
        defaultWidget: 'scoreByRound',
        triggeredWidgets: [],
        // Show score widget in normal gameplay, speed gun in test mode
        panelPredicate: (gameState) => {
            // Show in test mode OR when round >= 1 (game is active)
            return gameState.testMode === true;// || gameState.round >= 1;
        },
    } as PanelConfig,
    rightPanel: {
        timedWidgets: ['speedGun'],
        timedDuration: 99999999,
        defaultWidget: 'speedGun',
        triggeredWidgets: [],
        // Only show this panel in test mode
        // panelPredicate: (gameState) => gameState.testMode === true,
        panelPredicate: (gameState) => {
            // Show in test mode OR when round >= 1 (game is active)
            return gameState.testMode === true;// || gameState.round >= 1;
        },
    } as PanelConfig,
};
